import React, { type LegacyRef } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLeaf, faSeedling, faHeart } from '@fortawesome/free-solid-svg-icons';
import { useScrollReveal } from '../hooks/useScrollReveal';
import './home.scss';

const Home: React.FC = () => {
  const [valuesRef, valuesVisible] = useScrollReveal<HTMLElement>();
  const [bandRef, bandVisible] = useScrollReveal<HTMLElement>();
  const [stepsRef, stepsVisible] = useScrollReveal<HTMLElement>();

  return (
    <div className="home">
      <section className="home-hero" aria-labelledby="home-hero-heading">
        <div className="home-hero__inner">
          <p className="home-hero__eyebrow">Fresh · Sustainable · Local</p>
          <h1 id="home-hero-heading">GreenStore</h1>
          <p className="home-hero__lead">
            We bring together thoughtfully sourced goods and a calm, modern experience - so you can shop with confidence and care for the planet.
          </p>
          <div className="home-hero__actions">
            <Link className="home-btn home-btn--primary" to="/products">
              Shop products
            </Link>
            <Link className="home-btn home-btn--ghost" to="/about">
              Our story
            </Link>
          </div>
        </div>
      </section>

      <section
        ref={valuesRef as LegacyRef<HTMLElement>}
        className={`home-values home-reveal${valuesVisible ? ' home-reveal--visible' : ''}`}
        aria-labelledby="values-heading"
      >
        <div className="home-values__header">
          <h2 id="values-heading" className="home-values__title">
            <span className="home-values__title-prefix">Why</span>{' '}
            <span className="home-values__title-emphasis">shop with us</span>
          </h2>
        </div>
        <div className="home-values__grid">
          <article
            className="home-values__item"
            aria-labelledby="value-heading-sourcing"
            aria-describedby="value-desc-sourcing"
          >
            <div className="home-values__icon" aria-hidden>
              <FontAwesomeIcon icon={faLeaf} />
            </div>
            <h3 id="value-heading-sourcing">Responsible sourcing</h3>
            <p id="value-desc-sourcing">
              We prioritize suppliers who share our commitment to ethical and sustainable practices.
            </p>
          </article>
          <article
            className="home-values__item"
            aria-labelledby="value-heading-quality"
            aria-describedby="value-desc-quality"
          >
            <div className="home-values__icon" aria-hidden>
              <FontAwesomeIcon icon={faSeedling} />
            </div>
            <h3 id="value-heading-quality">Quality you can feel</h3>
            <p id="value-desc-quality">
              Every item is chosen for durability and everyday use - fewer replacements, less waste.
            </p>
          </article>
          <article
            className="home-values__item"
            aria-labelledby="value-heading-community"
            aria-describedby="value-desc-community"
          >
            <div className="home-values__icon" aria-hidden>
              <FontAwesomeIcon icon={faHeart} />
            </div>
            <h3 id="value-heading-community">Community minded</h3>
            <p id="value-desc-community">
              Built as a portfolio showcase with accessibility and real e-commerce patterns in mind.
            </p>
          </article>
        </div>
      </section>

      <section
        ref={bandRef as LegacyRef<HTMLElement>}
        className={`home-band home-reveal${bandVisible ? ' home-reveal--visible' : ''}`}
        aria-labelledby="band-heading"
      >
        <div className="home-band__inner">
          <h2 id="band-heading">Our collection</h2>
          <p>
            Browse the full catalog, search by name or description, and add items to your basket - same flow you know, wrapped in a refreshed look.
          </p>
          <Link className="home-btn home-btn--primary" to="/products">
            Browse all products
          </Link>
        </div>
      </section>

      <section
        ref={stepsRef as LegacyRef<HTMLElement>}
        className={`home-steps home-reveal${stepsVisible ? ' home-reveal--visible' : ''}`}
        aria-labelledby="steps-heading"
      >
        <h2 id="steps-heading" className="home-steps__title">
          <span className="home-steps__title-prefix">How it</span>{' '}
          <span className="home-steps__title-emphasis">works</span>
        </h2>
        <div className="home-steps__row">
          <div className="home-steps__step">
            <div className="home-steps__num" aria-hidden>
              1
            </div>
            <h3>Explore</h3>
            <p>Scroll the grid or use search to find what you need.</p>
          </div>
          <div className="home-steps__step">
            <div className="home-steps__num" aria-hidden>
              2
            </div>
            <h3>Add to basket</h3>
            <p>Open a product, choose a quantity, and add it to your basket.</p>
          </div>
          <div className="home-steps__step">
            <div className="home-steps__num" aria-hidden>
              3
            </div>
            <h3>Checkout</h3>
            <p>Review your basket and complete your demo order.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
